import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import * as api from "api/api";
import { loginReset } from "redux/slice/authentication";
import { arrayToById } from "redux/normalize";
import { AgentMlsIdResource, NewAgentMlsIdResource } from "api/resource/agent-mls-id";
import { ClientBoardResource } from "api/resource/client-board";
import { AgentBoard, NewAgentBoard } from "model/agent-board";
import { fetchClientBoards } from "redux/slice/client-boards";
import type { ThunkAppDispatch } from "redux/store";

// GET /agent-mls-ids.json. shared/agent-mls-ids builds everything from it, the client-boards
// read and the agents read.
export interface AgentMlsIdsState {
    loading?: boolean;
    error?: string | null;
    byId: {
        [key: number]: AgentMlsIdResource;
    };
};

const initialState: AgentMlsIdsState = {
    byId: {},
};

const fetchAgentMlsIds = createAsyncThunk("agentMlsIds/fetchAgentMlsIds", async (payload, thunkAPI) => {
    thunkAPI.dispatch(agentMlsIdsLoading());
    const result = api.getAgentMlsIds();
    result
        .then((agentMlsIds) => thunkAPI.dispatch(agentMlsIdsLoaded(agentMlsIds)))
        .catch((error) => thunkAPI.dispatch(agentMlsIdsFailed(error)));
    return result;
});

// A refused write (409) rejects with the rule's code, which the dialog words. The status is kept for a 404 (another
// account's id, or one deleted since), which a plain rejected thunk would drop. A failed write is the dialog's to show,
// so it doesn't set the slice's error, which would replace the whole page.
export interface MlsIdWriteError {
    status?: number;
    code?: string;
    message?: string;
}

type WriteConfig = { rejectValue: MlsIdWriteError };

const writeError = (error: unknown): MlsIdWriteError => {
    const e = error as { status?: number; message?: string };
    return { status: e && e.status, code: e && e.status === 409 ? e.message : undefined, message: e && e.message };
};

// Each write refetches both reads rather than updating the entry in place: a write changes the derived fields
// (slots, holder counts, aligned) of every row with the same value, which only the service works out.
const refetch = (dispatch: ThunkAppDispatch) => Promise.all([dispatch(fetchAgentMlsIds()), dispatch(fetchClientBoards())]);

const createAgentMlsId = createAsyncThunk<AgentMlsIdResource, { agentMlsId: NewAgentMlsIdResource }, WriteConfig>("agentMlsIds/createAgentMlsId", async (payload, thunkAPI) => {
    try {
        const result = await api.createAgentMlsId(payload.agentMlsId);
        await refetch(thunkAPI.dispatch as ThunkAppDispatch);
        return result;
    } catch(error) {
        return thunkAPI.rejectWithValue(writeError(error));
    }
});

const updateAgentMlsId = createAsyncThunk<AgentMlsIdResource, { id: number; agentMlsId: NewAgentMlsIdResource }, WriteConfig>("agentMlsIds/updateAgentMlsId", async (payload, thunkAPI) => {
    try {
        const result = await api.updateAgentMlsId(payload.id, payload.agentMlsId);
        await refetch(thunkAPI.dispatch as ThunkAppDispatch);
        return result;
    } catch(error) {
        return thunkAPI.rejectWithValue(writeError(error));
    }
});

// refetch false is for a fix run, which runs several writes and reloads once after the last.
const deleteAgentMlsId = createAsyncThunk<void, { id: number; refetch?: boolean }, WriteConfig>("agentMlsIds/deleteAgentMlsId", async (payload, thunkAPI) => {
    try {
        await api.deleteAgentMlsId(payload.id);
        if (payload.refetch !== false) {
            await refetch(thunkAPI.dispatch as ThunkAppDispatch);
        }
    } catch(error) {
        return thunkAPI.rejectWithValue(writeError(error));
    }
});

// The plain client-board and agent-board writes behind the MLS ID screens: a Featured row saved or taken off, an
// AgentBoard row added or deleted. Unlike the client-boards and agent-boards slices' own thunks, a failure here goes to
// the caller and doesn't set that slice's error or loading (which would replace the whole page), and strict is passed
// through. They don't refetch: the page or the fix run reloads after.
const updateMlsIdClientBoard = createAsyncThunk<ClientBoardResource, { clientBoard: ClientBoardResource; strict?: boolean }, WriteConfig>("agentMlsIds/updateMlsIdClientBoard", async (payload, thunkAPI) => {
    try {
        return await api.updateClientBoard(payload.clientBoard.id, payload.clientBoard, payload.strict);
    } catch(error) {
        return thunkAPI.rejectWithValue(writeError(error));
    }
});

const addMlsIdClientBoard = createAsyncThunk<ClientBoardResource, { clientBoard: ClientBoardResource; strict?: boolean }, WriteConfig>("agentMlsIds/addMlsIdClientBoard", async (payload, thunkAPI) => {
    try {
        return await api.addClientBoard(payload.clientBoard, payload.strict);
    } catch(error) {
        return thunkAPI.rejectWithValue(writeError(error));
    }
});

const createMlsIdAgentBoard = createAsyncThunk<AgentBoard, { agentBoard: NewAgentBoard }, WriteConfig>("agentMlsIds/createMlsIdAgentBoard", async (payload, thunkAPI) => {
    try {
        return await api.createAgentBoard(payload.agentBoard);
    } catch(error) {
        return thunkAPI.rejectWithValue(writeError(error));
    }
});

const deleteMlsIdAgentBoard = createAsyncThunk<AgentBoard, { agentBoard: AgentBoard }, WriteConfig>("agentMlsIds/deleteMlsIdAgentBoard", async (payload, thunkAPI) => {
    try {
        return await api.deleteAgentBoard(payload.agentBoard);
    } catch(error) {
        return thunkAPI.rejectWithValue(writeError(error));
    }
});

export const agentMlsIdsSlice = createSlice({
    name: "agentMlsIds",
    initialState,
    reducers: {
        agentMlsIdsLoading: (state) => {
            state.loading = true;
        },
        agentMlsIdsLoaded: (state, action: PayloadAction<AgentMlsIdResource[]>) => {
            state.loading = false;
            state.error = null;
            state.byId = arrayToById(action.payload, "id");
        },
        agentMlsIdsFailed: (state, action: PayloadAction<any>) => {
            state.loading = false;
            state.error = action.payload.message;
        },
    },
    extraReducers(builder) {
        builder.addCase(loginReset, (state) => {
            return {
                ...initialState,
                error: state.error,
            };
        });
    },
});

export const { agentMlsIdsLoading, agentMlsIdsLoaded, agentMlsIdsFailed } = agentMlsIdsSlice.actions;
export { fetchAgentMlsIds, createAgentMlsId, updateAgentMlsId, deleteAgentMlsId, updateMlsIdClientBoard, addMlsIdClientBoard, createMlsIdAgentBoard, deleteMlsIdAgentBoard };
